import { format, isValid, parse } from 'date-fns';
import { DateTime, Duration } from 'luxon';

export const dateTimeFormat = 'yyyy-MM-dd HH:mm:ss';

/** Parse and output dates like "Thursday, May 22, 2025" */
export const raTimelineLabelToDate = (label: string): string => {
  const date = DateTime.fromFormat(label, 'DDDD');
  return date.isValid ? date.toFormat('yyyy-MM-dd') : '';
};

/** Parse and output timestamps like 1622548800000 */
export const timestampToDateTime = (timestamp: number): string => {
  const date = DateTime.fromMillis(timestamp);
  return date.isValid ? date.toFormat('yyyy-MM-dd HH:mm:ss') : '';
};

/** Parse and output ISO 8601 strings like "2025-05-22T14:00:00Z" */
export const isoToDateTime = (isoString: string): string => {
  const date = DateTime.fromISO(isoString);
  return date.isValid ? date.toFormat('yyyy-MM-dd HH:mm:ss') : '';
};

/** Parse and output ISO 8601 strings as time only like "14:00:53" */
export const isoToTime = (isoString: string): string => {
  const date = DateTime.fromISO(isoString);
  return date.isValid ? date.toFormat('HH:mm:ss') : '';
};

/** Parse and output ISO 8601 strings as date only like "2025-05-22" */
export const isoToDate = (isoString: string): string => {
  const date = DateTime.fromISO(isoString);
  return date.isValid ? date.toFormat('yyyy-MM-dd') : '';
};

export interface LocalTimeHint {
  time: string;
  /** -1/+1 when the local wall-clock time falls on the previous/next day relative to the UTC one */
  dayOffset: -1 | 0 | 1;
}

/** Convert a "HH:mm" wall-clock time stored in UTC to the equivalent local time, with any day shift */
export const utcTimeToLocalHint = (value: string | null | undefined): LocalTimeHint | null => {
  if (value == null) return null;
  // anchored to a fixed mid-month day so a day shift can only ever be -1, 0 or +1
  const utc = DateTime.fromFormat(value, 'HH:mm', { zone: 'utc' }).set({ year: 2024, month: 6, day: 15 });
  if (!utc.isValid) return null;
  const local = utc.toLocal();
  return { time: local.toFormat('HH:mm'), dayOffset: Math.sign(local.day - utc.day) as -1 | 0 | 1 };
};

/** Parse "HH:mm" string to Date for the OrdTimeInput picker */
export const parseTimeToDate = (value: string | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  const parsed = parse(value, 'HH:mm', new Date());
  return isValid(parsed) ? parsed : null;
};

/** Format Date to "HH:mm" string for the form value */
export const formatDateToTime = (date: Date | null): string | null =>
  date && isValid(date) ? format(date, 'HH:mm') : null;

/** Calculate duration between two ISO 8601 timestamps and format as "hh:mm:ss" */
export const isoDuration = (startIso: string, endIso: string): string => {
  const start = DateTime.fromISO(startIso);
  const end = DateTime.fromISO(endIso);
  if (!start.isValid || !end.isValid) return '';
  return end.diff(start).toFormat('hh:mm:ss');
};

/** Convert seconds to duration format "hh:mm:ss" */
export const secondsToDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '';
  const duration = Duration.fromObject({ seconds });
  return duration.toFormat('hh:mm:ss');
};
